/**
 * useFriendsActions hook
 * Extracted from app/friends.tsx
 *
 * Manages friend-related actions: removing friends, updating picks, selecting picks, toggling cards
 * Uses React Query mutations for all API calls
 */

import { useCallback, useState } from 'react'
import { useDialog } from '../../shared/hooks/useDialog'
import {
  isPendingInvitation,
  getRemovalConfirmationMessage,
  getRemovalAlertTitle,
  getRemovalButtonText,
  getRemovalSuccessMessage,
  findChangedMatches,
  buildPredictionsUpdatePayload,
  updateOriginalSelections,
} from '../helpers/friendsActions.helper'
import {
  useRemoveFriend,
  useUpdatePredictions,
  useAcceptInvitation,
  useDeclineInvitation,
} from './useFriendsMutations'
import type {
  Friend,
  FriendMatchSummary,
  BetSelection,
} from '../types/friends.types'
import type { SnackbarVariant } from '../../shared/stores/snackbarStore'

interface UseFriendsActionsProps {
  currentUserId: string
  // From useFriendsMatchesData
  getFriendMatches: (friendId: string) => FriendMatchSummary[]
  expandedFriendId: string | null
  setExpandedFriendId: React.Dispatch<React.SetStateAction<string | null>>
  userSelections: Record<string, BetSelection>
  setUserSelections: React.Dispatch<
    React.SetStateAction<Record<string, BetSelection>>
  >
  originalSelections: Record<string, BetSelection>
  setOriginalSelections: React.Dispatch<
    React.SetStateAction<Record<string, BetSelection>>
  >
  updatingFriendId: string | null
  setUpdatingFriendId: React.Dispatch<React.SetStateAction<string | null>>
  refreshFriends: () => Promise<void> // Function to refresh friends from global context
  setSnackbar: React.Dispatch<
    React.SetStateAction<{ message: string; variant: SnackbarVariant } | null>
  >
}

interface UseFriendsActionsReturn {
  handleRemoveFriend: (friendId: string, friend?: Friend) => void
  handleUpdateFriendMatches: (friendId: string) => Promise<void>
  handlePickSelect: (
    matchId: string,
    selection: Exclude<BetSelection, null>
  ) => void
  toggleFriendCard: (friendId: string) => void
  handleAcceptInvitation: (token: string, invitationId: string) => Promise<void>
  handleDeclineInvitation: (invitationId: string) => Promise<void>
  actionLoadingId: string | null
}

export const useFriendsActions = ({
  currentUserId,
  getFriendMatches,
  expandedFriendId,
  setExpandedFriendId,
  userSelections,
  setUserSelections,
  originalSelections,
  setOriginalSelections,
  setUpdatingFriendId,
  refreshFriends,
  setSnackbar,
}: UseFriendsActionsProps): UseFriendsActionsReturn => {
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const { showDialog, showAlert } = useDialog()

  // React Query mutations
  const removeFriendMutation = useRemoveFriend()
  const updatePredictionsMutation = useUpdatePredictions()
  const acceptInvitationMutation = useAcceptInvitation()
  const declineInvitationMutation = useDeclineInvitation()
  const handleRemoveFriend = useCallback(
    async (friendId: string, friend?: Friend) => {
      // Close expanded card immediately when button is clicked
      if (expandedFriendId === friendId) {
        setExpandedFriendId(null)
      }

      // Check if it's a pending invitation using helper
      const isPending = isPendingInvitation(friendId, friend)

      // Get confirmation message using helper
      const confirmMessage = getRemovalConfirmationMessage(isPending)
      const alertTitle = getRemovalAlertTitle(isPending)
      const buttonText = getRemovalButtonText(isPending)

      // Use global dialog system
      const confirmed = await showDialog({
        title: alertTitle,
        message: confirmMessage,
        type: 'danger',
        confirmText: buttonText,
        cancelText: 'Cancel',
      })

      if (!confirmed) {
        return
      }

      // User confirmed, proceed with deletion using React Query mutation
      try {
        await removeFriendMutation.mutateAsync(friendId)

        // Show success message using helper
        setSnackbar({
          message: getRemovalSuccessMessage(isPending),
          variant: 'success',
        })

        // Refresh friends from global context to update the list
        // React Query will also invalidate the cache automatically
        await refreshFriends()

        // Small delay to ensure state propagates
        await new Promise((resolve) => setTimeout(resolve, 100))
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : `An error occurred while ${
                isPending ? 'cancelling invitation' : 'removing friend'
              }`
        await showAlert({
          title: 'Error',
          message: errorMessage,
          type: 'danger',
        })
      }
    },
    [
      expandedFriendId,
      setExpandedFriendId,
      showDialog,
      showAlert,
      setSnackbar,
      refreshFriends,
      removeFriendMutation,
    ]
  )

  const toggleFriendCard = useCallback(
    (friendId: string) => {
      setExpandedFriendId((prev) => (prev === friendId ? null : friendId))
    },
    [setExpandedFriendId]
  )

  const handlePickSelect = useCallback(
    (matchId: string, selection: Exclude<BetSelection, null>) => {
      setUserSelections((prev) => {
        const current = prev[matchId] ?? null
        const nextSelection = current === selection ? null : selection
        return {
          ...prev,
          [matchId]: nextSelection,
        }
      })
    },
    [setUserSelections]
  )

  const handleUpdateFriendMatches = useCallback(
    async (friendId: string) => {
      const friendMatches = getFriendMatches(friendId)
      if (!friendMatches.length) return

      // Find changed matches using helper
      const changedMatches = findChangedMatches(
        friendMatches,
        userSelections,
        originalSelections
      )

      // Build predictions payload using helper
      const predictionsToUpdate = buildPredictionsUpdatePayload(
        changedMatches,
        userSelections,
        currentUserId
      )

      if (!predictionsToUpdate.length) {
        return
      }

      setUpdatingFriendId(friendId)
      try {
        await updatePredictionsMutation.mutateAsync(predictionsToUpdate)

        // Update original selections using helper
        setOriginalSelections((prev) =>
          updateOriginalSelections(prev, changedMatches, userSelections)
        )
      } catch (error) {
        // Surface error feedback to the user
        setSnackbar({
          message:
            error instanceof Error
              ? error.message
              : 'Failed to update predictions',
          variant: 'error',
        })
      } finally {
        setUpdatingFriendId(null)
      }
    },
    [
      getFriendMatches,
      userSelections,
      originalSelections,
      currentUserId,
      setUpdatingFriendId,
      setOriginalSelections,
      updatePredictionsMutation,
      setSnackbar,
    ]
  )

  const handleAcceptInvitation = useCallback(
    async (token: string, invitationId: string) => {
      // Guard clause: Validate token before proceeding
      if (!token) {
        setSnackbar({
          message: 'Invalid invitation token.',
          variant: 'error',
        })
        return
      }

      // Guard clause: Validate invitationId
      if (!invitationId) {
        setSnackbar({
          message: 'Invalid invitation ID.',
          variant: 'error',
        })
        return
      }

      setActionLoadingId(invitationId)
      try {
        await acceptInvitationMutation.mutateAsync(token)

        // Optimistic: Refresh immediately for instant UI update
        // React Query will also invalidate the cache automatically
        await refreshFriends()
        setSnackbar({
          message: 'Friend request accepted!',
          variant: 'success',
        })
      } catch (error) {
        setSnackbar({
          message:
            error instanceof Error
              ? error.message
              : 'Network error. Please try again.',
          variant: 'error',
        })
      } finally {
        setActionLoadingId(null)
      }
    },
    [acceptInvitationMutation, refreshFriends, setSnackbar]
  )

  const handleDeclineInvitation = useCallback(
    async (invitationId: string) => {
      // Guard clause: Validate invitationId
      if (!invitationId) {
        setSnackbar({
          message: 'Invalid invitation ID.',
          variant: 'error',
        })
        return
      }

      setActionLoadingId(invitationId)
      try {
        await declineInvitationMutation.mutateAsync(invitationId)

        // React Query will also invalidate the cache automatically
        await refreshFriends()
        setSnackbar({
          message: 'Invitation declined',
          variant: 'success',
        })
      } catch (error) {
        setSnackbar({
          message:
            error instanceof Error
              ? error.message
              : 'Network error. Please try again.',
          variant: 'error',
        })
      } finally {
        setActionLoadingId(null)
      }
    },
    [declineInvitationMutation, refreshFriends, setSnackbar]
  )

  return {
    handleRemoveFriend,
    handleUpdateFriendMatches,
    handlePickSelect,
    toggleFriendCard,
    handleAcceptInvitation,
    handleDeclineInvitation,
    actionLoadingId,
  }
}
